import { useEffect } from 'react';

// Tab rows that are wider than a phone screen: the underline tabs of the legacy
// pages (.property-navbar / .custom-navbar) and the segmented .btn-group switches.
// They scroll sideways, but nothing showed that more tabs were there, and the
// active tab could sit off-screen. This marks each row with data-fade="left|right|both"
// (styled in MobileTabBar.css) and scrolls the active tab into view.
const ROW_SELECTOR = '.property-navbar, .custom-navbar, .tenant-type-cards-row, .tenant-type-selection-add-tenant, .btn-group[role="group"]';
const ACTIVE_SELECTOR = '.active, .btn-primary';

const updateFade = (row) => {
  const overflow = row.scrollWidth - row.clientWidth;
  if (overflow <= 2) {
    row.removeAttribute('data-fade');
    return;
  }
  const atStart = row.scrollLeft <= 2;
  const atEnd = row.scrollLeft >= overflow - 2;
  row.setAttribute('data-fade', atStart ? 'right' : atEnd ? 'left' : 'both');
};

const centerActive = (row) => {
  const active = row.querySelector(ACTIVE_SELECTOR);
  if (!active || row.scrollWidth <= row.clientWidth) return;
  const rowBox = row.getBoundingClientRect();
  const box = active.getBoundingClientRect();
  if (box.left >= rowBox.left && box.right <= rowBox.right) return;
  row.scrollLeft += box.left - rowBox.left - (rowBox.width - box.width) / 2;
};

// Only plain switches: a .btn-group that holds a dropdown would get its menu clipped.
const isTabRow = (row) => !row.matches('.btn-group') || (row.children.length >= 2 && !row.querySelector('.dropdown-menu, .dropdown-toggle'));

const scan = (centre) => {
  document.querySelectorAll(ROW_SELECTOR).forEach((row) => {
    if (!isTabRow(row)) return;
    row.classList.add('mobile-tab-row');
    if (centre) centerActive(row);
    updateFade(row);
  });
};

const useMobileTabRows = (pathname, enabled) => {
  useEffect(() => {
    if (!enabled) return undefined;
    // Pages render their tabs after their data arrives, so look again a few times.
    const timers = [150, 600, 1500].map((delay) => setTimeout(() => scan(true), delay));
    return () => timers.forEach(clearTimeout);
  }, [pathname, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onScroll = (event) => {
      const target = event.target;
      if (target instanceof Element && target.classList.contains('mobile-tab-row')) updateFade(target);
    };
    const onResize = () => scan(false);
    // Clicking a tab re-renders the row with a new active tab.
    const onClick = (event) => {
      const row = event.target instanceof Element && event.target.closest('.mobile-tab-row');
      if (row) setTimeout(() => { centerActive(row); updateFade(row); }, 50);
    };
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('click', onClick, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('resize', onResize);
    };
  }, [enabled]);
};

export default useMobileTabRows;
